import { flatten, last, partition, isEmpty } from 'lodash'
import type { BaseDataItem, OuterVariant, RepeaterVariant, VariantDataItem, InnerVariant } from '@wix/thunderbolt-becky-types'
import type { VariantRelationViewItem, CompVariantsViewItem, RefArrayViewItem } from '@wix/thunderbolt-catharsis'
import { getDisplayedId, getTemplateItemId, isDisplayedOnly } from '@wix/thunderbolt-commons'
import type { PatternsMap } from '../features/cssFeature.types'
import {
	getTemplateRepeaterIdSelector,
	getRegularIdSelector,
	getInflatedRepeaterIdSelector,
	getInflatedDynamicSlotIdSelector,
} from './selectorsUtils'
import type { CssContext, GetParentCb } from '../features/componentCss.types'
import { getVariantsOrderScore } from '@wix/thunderbolt-becky-root'
import { EMPTY_ARRAY } from './constants'
import { isBreakpointRange } from './is'

export const VARIANTS_SEPARATOR = '$$$'
const COMP_SELECTORS_SEPARATOR = ' '
type VariantSelectorsParams = {
	isSlot: boolean
	isInRepeater: boolean
	isMobileView?: boolean
	patterns?: PatternsMap
}
const VARIANT_SELECTOR: Record<
	string,
	<T extends VariantDataItem>(variant: T, params?: VariantSelectorsParams) => string | Array<string>
> = {
	Hover: (variant, params) => {
		const isEditor = process.env.PACKAGE_NAME === 'thunderbolt-ds'
		const isMobileView = !!params?.isMobileView

		if (isEditor) {
			const selectors = [`.${variant.id}:where([data-view-mode=editor] *)`]
			if (!isMobileView) {
				selectors.unshift(':hover')
			}

			return selectors
		}

		if (isMobileView) {
			return []
		}

		return ':hover'
	},

	Class: (variant) => `.${variant.id}`,
	State: (variant) => `.${variant.id}`,
	Preset: (variant) => `.${variant.id}`,
	Selected: (variant) => `.${variant.id}`,
	Mobile: () => '.device-mobile-optimized',
	RepeaterPattern: (variant, params) =>
		params?.patterns?.[(variant as RepeaterVariant).componentId]?.nthChildren?.[variant.id] || [],
}

export const isRepeaterPattern = (variant: InnerVariant) => variant.type === 'RepeaterPattern'

const variantHasSpecificity = (variant: InnerVariant) => !isRepeaterPattern(variant)

const getInflatedRepeaterId = (compIdToFix: string, targetId: string) => {
	const itemId = getTemplateItemId(targetId)
	if (itemId) {
		compIdToFix = getDisplayedId(compIdToFix, itemId)
	}
	return compIdToFix
}

const outerVariants = ['Preset', 'Mobile']

const getIdSelector = (
	compId: string,
	targetId: string,
	isRepeaterTemplate: boolean,
	isSlot: boolean,
	isDynamicSlotExperimentOpen: boolean,
	shouldTargetBuilderWrapper: boolean,
	supportExternalDependenciesCss: boolean = false,
	shouldForceIdSelector: boolean = false
) => {
	if (!compId) {
		return ''
	}
	const maybeInflatedId = getInflatedRepeaterId(compId, targetId)

	const isInflatedChildOfRepeater = isDisplayedOnly(maybeInflatedId)

	if (isInflatedChildOfRepeater) {
		return getInflatedRepeaterIdSelector(maybeInflatedId)
	}

	if (isRepeaterTemplate) {
		return getTemplateRepeaterIdSelector(maybeInflatedId)
	}
	if (isDynamicSlotExperimentOpen && isSlot) {
		return getInflatedDynamicSlotIdSelector(maybeInflatedId, shouldTargetBuilderWrapper, supportExternalDependenciesCss)
	}

	return getRegularIdSelector(maybeInflatedId, shouldTargetBuilderWrapper, supportExternalDependenciesCss, shouldForceIdSelector)
}

const sortVariantsByTreeStructure = (variantsGroupedByCompId: Array<Array<InnerVariant>>, getParent?: GetParentCb) => {
	const variantsOnSingleComp = variantsGroupedByCompId.length < 2
	if (variantsOnSingleComp) {
		return variantsGroupedByCompId
	}
	const compIdsInVariants = variantsGroupedByCompId.map((groupedVariants) => groupedVariants[0].componentId)

	let curr = getParent?.()
	const sortedVariantsGroupedByCompId = []
	while (curr && sortedVariantsGroupedByCompId.length < variantsGroupedByCompId.length) {
		const compIdIndex = compIdsInVariants.indexOf(curr.componentId)
		if (compIdIndex >= 0) {
			sortedVariantsGroupedByCompId.unshift(variantsGroupedByCompId[compIdIndex])
		}
		curr = curr.getParent?.()
	}
	return sortedVariantsGroupedByCompId
}

const getVariantsGroupedByCompId = (
	variantIds: Array<string>,
	variants: CompVariantsViewItem,
	patterns?: PatternsMap,
	getParent?: GetParentCb
) => {
	if (!variantIds.length) {
		return []
	}

	const compIdToIndex: { [key: string]: number } = {}

	// group by variant's component ID while maintaining the variants' order
	const variantsGroupedByCompId = variantIds.reduce((acc: Array<Array<InnerVariant>>, variantId) => {
		const variant = variants[variantId] as InnerVariant
		const componentId = isRepeaterPattern(variant)
			? (patterns as PatternsMap)![variant.componentId]!.componentId
			: variant.componentId

		const componentVariantsIndex = compIdToIndex[componentId] >= 0 ? compIdToIndex[componentId] : acc.length
		compIdToIndex[componentId] = componentVariantsIndex

		acc[componentVariantsIndex] = acc[componentVariantsIndex] || []
		acc[componentVariantsIndex].push({ ...variant, componentId })
		return acc
	}, [])

	return sortVariantsByTreeStructure(variantsGroupedByCompId, getParent)
}

const resolveSelectors = (
	variant: VariantDataItem,
	variants: CompVariantsViewItem,
	variantSelectorParams?: VariantSelectorsParams | undefined
) => {
	const selectors = VARIANT_SELECTOR[variant.type](variants[variant.id], variantSelectorParams)
	return Array.isArray(selectors) ? selectors : [selectors]
}

const selectorsArrayToStr = (selectorsArr: Array<Array<string>> = []) =>
	selectorsArr.reduce(
		(acc: Array<string>, selectors: Array<string>) =>
			flatten(selectors.map((selector) => acc.map((accSelector) => `${accSelector}${selector}`))),
		['']
	)

const getCompSelector = (
	targetId: string,
	variantsInComp: Array<InnerVariant>,
	isInRepeater: boolean,
	isSlot = false,
	isDynamicSlotExperimentOpen: boolean,
	shouldTargetBuilderWrapper: boolean
) => {
	const [{ componentId, type }] = variantsInComp

	if (outerVariants.includes(type)) {
		return ''
	}

	const compSelectorShouldHaveSpecificity = targetId === componentId || variantsInComp.some(variantHasSpecificity)
	const compIdSelector = getIdSelector(
		componentId,
		targetId,
		isInRepeater,
		isSlot,
		isDynamicSlotExperimentOpen,
		shouldTargetBuilderWrapper
	)

	return compSelectorShouldHaveSpecificity ? compIdSelector : `:where(${compIdSelector})`
}

const getVariantSelector = (
	variant: InnerVariant,
	variants: CompVariantsViewItem,
	variantSelectorParams: VariantSelectorsParams
) => {
	const variantSelectors = resolveSelectors(variant, variants, variantSelectorParams)
	const isVariantHasSpecificity = variantHasSpecificity(variant)
	return variantSelectors.map((variantSelector) =>
		isVariantHasSpecificity ? `${variantSelector}` : `:where(${variantSelector})`
	)
}
const getVariantsSelectorsArr = (
	targetId: string,
	groupedVariantsByComp: Array<Array<InnerVariant>>,
	variants: CompVariantsViewItem,
	variantSelectorParams: VariantSelectorsParams,
	isDynamicSlotExperimentOpen: boolean,
	shouldTargetBuilderWrapper: boolean
) => {
	const { isInRepeater, isSlot } = variantSelectorParams
	const selectorsArr: Array<Array<string>> = []
	groupedVariantsByComp.forEach((variantsInComp) => {
		const [{ componentId }] = variantsInComp
		const compSelector = getCompSelector(
			targetId,
			variantsInComp,
			isInRepeater,
			isSlot,
			isDynamicSlotExperimentOpen,
			shouldTargetBuilderWrapper
		)
		compSelector && selectorsArr.push([compSelector])

		variantsInComp.forEach((variant) =>
			selectorsArr.push(getVariantSelector(variant, variants, variantSelectorParams))
		)

		if (componentId !== targetId) {
			selectorsArr.push([COMP_SELECTORS_SEPARATOR])
		}
	})
	return selectorsArr
}

export const getVariantIds = (variantKey: string) => variantKey.split(VARIANTS_SEPARATOR)

/**
 * Detailed logic explanation in variants.md
 */
export const getInnerVariantSelectors = (
	variantIds: Array<string>,
	variants: CompVariantsViewItem,
	targetId: string,
	idTargetSelector: string,
	cssContext: CssContext,
	isDynamicSlotExperimentOpen: boolean,
	shouldTargetBuilderWrapper: boolean
) => {
	const {
		patterns,
		getParent,
		isInRepeater = false,
		isStylable = false,
		isMobileView = false,
		isSlot = false,
	} = cssContext

	const groupedVariantsByComp = getVariantsGroupedByCompId(variantIds, variants, patterns, getParent)

	const selectorsArr = getVariantsSelectorsArr(
		targetId,
		groupedVariantsByComp,
		variants,
		{
			isSlot,
			patterns,
			isMobileView,
			isInRepeater,
		},
		isDynamicSlotExperimentOpen,
		shouldTargetBuilderWrapper
	)

	const lastGroupVariant = groupedVariantsByComp.length ? last(groupedVariantsByComp) : null
	const lastVariantCompId = lastGroupVariant ? lastGroupVariant[0].componentId : ''

	const hasSelectorOnTargetComp = lastVariantCompId === targetId

	const hasVariantWithSpecifiy = variantIds.some((variantId) =>
		variantHasSpecificity(variants[variantId] as InnerVariant)
	)

	const shouldHaveTargetSelector = !isStylable || !hasVariantWithSpecifiy
	// stylable components don't need to be targeted with #compId since it has unique class names so we can
	// reduce specifity of the interaction rule to support interactions on stylable components
	if (!hasSelectorOnTargetComp && shouldHaveTargetSelector) {
		selectorsArr.push([idTargetSelector])
	}

	return selectorsArr
}

const splitConditions = (
	variantIds: Array<string>,
	variants: CompVariantsViewItem | null
): [Array<string>, Array<string>] => {
	return variants && !isEmpty(variants)
		? partition(variantIds, (variantId) => {
			return outerVariants.includes(variants[variantId].type)
		})
		: [[], []]
}

const getOuterVariantSelectors = (outerVariantsIds: Array<string>, variants: CompVariantsViewItem) => {
	if (!outerVariantsIds.length) {
		return []
	}
	const selectorArr = outerVariantsIds.map((variantId) => {
		const variant = variants[variantId] as OuterVariant
		return resolveSelectors(variant, variants as CompVariantsViewItem) // we don't need patterns in outer variants
	})

	selectorArr.push([COMP_SELECTORS_SEPARATOR])
	return selectorArr
}

/**
 * Resolves selectors for interactive elements within repeater patterns.
 *
 * For components in repeaters with nth-child patterns, this function generates:
 * 1. Wrapper selectors: Transform selectors to target components inside wix-interact-element
 * 2. Non-wrapper selectors: Ensure original selectors only match when NOT inside wix-interact-element
 *
 * Both selector types maintain the same specificity as the original selectors.
 *
 * @param selectors - Original selectors to transform
 * @param isInRepeater - Whether the component is in a repeater
 * @returns Combined array of wrapper and non-wrapper selectors, or original selectors if no transformation needed
 */
const resolveRepeaterInteractiveElementSelectors = (selectors: Array<string>, isInRepeater: boolean): Array<string> => {
	// Skip transformation if not in repeater or no selectors or interaction experiments are off
	const areInteractionExperimentsOn =
		(globalThis as any).interactionsFeatureExperiment

	if (!isInRepeater || selectors.length === 0 || !areInteractionExperimentsOn) {
		return selectors
	}

	// Generate wrapper selectors for components with wix-interact-element
	const interactiveComponentSelectors = selectors
		.map((selector) => {
			// Transform selectors like [id^="comp-..."]:where(:nth-child(2n+1))
			// to :where(wix-interact-element:nth-child(2n+1)) > [id^="comp-..."]
			// Wrap wix-interact-element in :where() to maintain original specificity
			const nthChildMatch = selector.match(/^(.+?):where\((:nth-child\([^)]+\))\)(.*)$/)
			if (nthChildMatch) {
				const [, prefix, nthChild, suffix] = nthChildMatch
				return `:where(wix-interact-element${nthChild}) > ${prefix}${suffix}`.trim()
			}
			return null
		})
		.filter(Boolean) as Array<string>

	// For components with nth-child patterns in repeaters, generate both:
	// 1. Wrapper selectors (for components with wix-interact-element)
	// 2. Non-wrapper selectors (for components without wix-interact-element)
	// Both maintain the same specificity as the original selectors
	if (interactiveComponentSelectors.length > 0) {
		// Make original selectors more specific to prevent SSR interference
		// by ensuring they only match when NOT inside wix-interact-element
		// Wrap extra specificity in :where() to maintain original specificity
		const nonInteractiveCompSelectors = selectors.map((selector) => {
			const nthChildMatch = selector.match(/^(.+?):where\((:nth-child\([^)]+\))\)(.*)$/)
			if (nthChildMatch) {
				const [, prefix, nthChild, suffix] = nthChildMatch
				// Add :where(:not()) to ensure it only matches when not inside wix-interact-element
				// while maintaining same specificity as original
				return `${prefix}:where(${nthChild}:not(wix-interact-element *))${suffix}`.trim()
			}
			return selector
		})

		return [...interactiveComponentSelectors, ...nonInteractiveCompSelectors]
	}

	return selectors
}

export const getVariantSelectors = (
	variantKey: string,
	variants: CompVariantsViewItem | null,
	compId: string,
	cssContext: CssContext,
	{
		shouldTargetBuilderWrapper = false,
		supportExternalDependenciesCss = false,
		targetId = compId,
	}: {
		shouldTargetBuilderWrapper?: boolean
		supportExternalDependenciesCss?: boolean
		targetId?: string
	} = {}
) => {
	// @ts-ignore
	const isDynamicSlotExperimentOpen = globalThis.isDynamicSlotExperimentOpen

	const { isInRepeater = false, isSlot = false, shouldForceIdSelector = false } = cssContext

	const idTargetSelector = getIdSelector(
		compId,
		targetId,
		isInRepeater,
		isSlot,
		isDynamicSlotExperimentOpen,
		shouldTargetBuilderWrapper,
		supportExternalDependenciesCss,
		shouldForceIdSelector
	)

	// Checking if we have a variantKey, if not we return the targetId selector
	if (!variantKey) {
		return [idTargetSelector]
	}

	const variantIds = getVariantIds(variantKey)

	if (!variantIds.length) {
		return [idTargetSelector]
	}

	const [outerVariantsIds, innerVariantsIds] = splitConditions(variantIds, variants)
	const outerSelectorsArr = getOuterVariantSelectors(outerVariantsIds, variants!)
	const innerSelectorArr = getInnerVariantSelectors(
		innerVariantsIds,
		variants!,
		targetId,
		idTargetSelector,
		cssContext,
		isDynamicSlotExperimentOpen,
		shouldTargetBuilderWrapper
	)
	const selectorsArr = outerSelectorsArr.concat(innerSelectorArr)
	const selectors = selectorsArrayToStr(selectorsArr)

	// Resolve selectors for interactive elements in repeater patterns
	return resolveRepeaterInteractiveElementSelectors(selectors, isInRepeater)
}

export const isRefArray = <T extends BaseDataItem>(item?: BaseDataItem): item is RefArrayViewItem<T> =>
	item?.type === 'RefArray'
export const isVariantRelation = <TItem>(item: BaseDataItem): item is VariantRelationViewItem<TItem> =>
	item.type === 'VariantRelation'

export const variantsComparator = (themeVariants: CompVariantsViewItem) => (
	[variantAKey]: [string, any],
	[variantBKey]: [string, any]
) => {
	const variantsIdsInA = variantAKey ? variantAKey.split(VARIANTS_SEPARATOR) : EMPTY_ARRAY
	const variantsIdsInB = variantBKey ? variantBKey.split(VARIANTS_SEPARATOR) : EMPTY_ARRAY

	const scoreDiff =
		getVariantsOrderScore(variantsIdsInA, themeVariants) - getVariantsOrderScore(variantsIdsInB, themeVariants)

	return scoreDiff || variantsIdsInA.length - variantsIdsInB.length
}

export const getVariantKey = (variants: CompVariantsViewItem) =>
	Object.values(variants)
		.filter((variant) => !isBreakpointRange(variant))
		.map((variant) => variant.id)
		.join(VARIANTS_SEPARATOR)
